import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Pressable,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';

import { useAuth } from '../../contexts/AuthContext';
import { getAuthErrorMessage } from '../../utils/authErrors';
import type { AuthStackParamList } from '../../navigation/types';

type Props = NativeStackScreenProps<AuthStackParamList, 'Register'>;

export function RegisterScreen({ navigation }: Props) {
  const { register, signInAsGuest } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordAgain, setPasswordAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [guestBusy, setGuestBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acceptedLegal, setAcceptedLegal] = useState(false);

  async function onGuest() {
    setError(null);
    setGuestBusy(true);
    try {
      await signInAsGuest();
    } catch (e: unknown) {
      setError(getAuthErrorMessage(e));
    } finally {
      setGuestBusy(false);
    }
  }

  async function onSubmit() {
    setError(null);
    if (!acceptedLegal) {
      setError('Devam etmek için Kullanım Koşulları ve Gizlilik Politikasını okuyup kabul etmelisin.');
      return;
    }
    if (password.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır.');
      return;
    }
    if (password !== passwordAgain) {
      setError('Şifreler eşleşmiyor.');
      return;
    }
    setBusy(true);
    try {
      await register(email, password);
    } catch (e: unknown) {
      setError(getAuthErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      className="flex-1 bg-white dark:bg-black">
      <ScrollView
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ flexGrow: 1, paddingHorizontal: 24, paddingTop: 48, paddingBottom: 40 }}>
 <Text className="text-3xl font-bold text-gray-800 mb-1">Hesap oluştur</Text>
 <Text className="text-gray-500 text-base mb-8">
          Birkaç bilgiyle topluluğa katıl.
        </Text>

        {error ? (
 <View className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
 <Text className="text-center text-sm text-red-700">{error}</Text>
          </View>
        ) : null}

 <Text className="text-gray-800 font-medium mb-2">E-posta</Text>
        <TextInput
 className="mb-4 rounded-2xl border border-zinc-200 bg-white px-4 py-3.5 text-base text-gray-800"
          placeholder="ornek@email.com"
          placeholderTextColor="#9ca3af"
          autoCapitalize="none"
          keyboardType="email-address"
          autoCorrect={false}
          value={email}
          onChangeText={t => {
            setError(null);
            setEmail(t);
          }}
        />

 <Text className="text-gray-800 font-medium mb-2">Şifre</Text>
        <TextInput
 className="mb-4 rounded-2xl border border-zinc-200 bg-white px-4 py-3.5 text-base text-gray-800"
          placeholder="En az 6 karakter"
          placeholderTextColor="#9ca3af"
          secureTextEntry
          value={password}
          onChangeText={t => {
            setError(null);
            setPassword(t);
          }}
        />

 <Text className="text-gray-800 font-medium mb-2">Şifre tekrar</Text>
        <TextInput
 className="mb-6 rounded-2xl border border-zinc-200 bg-white px-4 py-3.5 text-base text-gray-800"
          placeholder="Şifreni tekrar gir"
          placeholderTextColor="#9ca3af"
          secureTextEntry
          value={passwordAgain}
          onChangeText={t => {
            setError(null);
            setPasswordAgain(t);
          }}
        />

        <View className="mb-6 flex-row gap-3">
          <Pressable
            accessibilityRole="checkbox"
            accessibilityState={{ checked: acceptedLegal }}
            onPress={() => {
              setError(null);
              setAcceptedLegal(v => !v);
            }}
            hitSlop={8}
            className="pt-0.5 active:opacity-70">
            <View
              className={`h-6 w-6 items-center justify-center rounded-lg border-2 ${
                acceptedLegal
                  ? 'border-zinc-900 bg-zinc-900 dark:border-zinc-100 dark:bg-zinc-100'
                  : 'border-zinc-300 bg-white dark:border-zinc-600 dark:bg-zinc-900'
              }`}>
              {acceptedLegal ? (
                <Text className="text-sm font-bold text-white dark:text-zinc-900">✓</Text>
              ) : null}
            </View>
          </Pressable>
          <Text className="flex-1 text-sm leading-5 text-zinc-700 dark:text-zinc-300">
            <Text
              onPress={() => navigation.navigate('TermsOfService')}
              className="font-semibold text-rose-700 underline dark:text-rose-400">
              Kullanım Koşulları
            </Text>
            <Text> (EULA) ve </Text>
            <Text
              onPress={() => navigation.navigate('PrivacyPolicy')}
              className="font-semibold text-rose-700 underline dark:text-rose-400">
              Gizlilik Politikası
            </Text>
            <Text>&apos;nı okudum, kabul ediyorum.</Text>
          </Text>
        </View>

        <TouchableOpacity
          activeOpacity={0.85}
          onPress={() => void onSubmit()}
          disabled={busy}
          className="mb-4 rounded-2xl border border-zinc-900 bg-zinc-900 py-4 dark:border-zinc-50 dark:bg-zinc-50">
          <Text className="text-center text-base font-semibold text-white dark:text-zinc-950">
            {busy ? 'Kayıt yapılıyor…' : 'Kayıt Ol'}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          activeOpacity={0.7}
          onPress={() => navigation.goBack()}
 className="py-3">
 <Text className="text-center text-base text-gray-500">
            Zaten hesabın var mı?{' '}
            <Text className="font-semibold text-zinc-900 dark:text-zinc-100">Giriş Yap</Text>
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          activeOpacity={0.75}
          onPress={() => void onGuest()}
          disabled={busy || guestBusy}
 className="mt-4 py-3">
          <Text className="text-center text-sm text-zinc-500 underline decoration-zinc-300 dark:text-zinc-400 dark:decoration-zinc-600">
            {guestBusy ? 'Misafir olarak açılıyor…' : 'Kayıt Olmadan Misafir Olarak Gez'}
          </Text>
        </TouchableOpacity>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}
